import * as React from 'react';
import { isZendeskEnabled, loadZendesk, type ZendeskApi } from './zendesk';

export type WidgetStatus = 'loading' | 'ready' | 'unavailable';

export interface SupportChatState {
  status: WidgetStatus;
  isOpen: boolean;
  unread: number;
  toggle: () => void;
}

/**
 * Drives the Zendesk messenger from our own button, the way AGRI's top bar does
 * (`farm-manager/web_client/app/containers/App/index.js`): Spanish locale, the
 * unread counter, and open/close kept in sync with the widget's own events — the
 * conversation window has its own close button, and our state must follow it.
 *
 * Unlike AGRI, the widget's floating launcher is hidden for the whole session
 * and only shown while the conversation is open, so the header button is the
 * single way in.
 *
 * **Identity.** Once the widget is up, `fetchToken` asks the backend
 * (`POST /api/account/support-chat/token`) for a messaging JWT and hands it to
 * `loginUser`, so support sees who is writing and the conversation follows the
 * person across browsers. AGRI signs that JWT in the browser with a secret
 * compiled into its bundle; here the secret never leaves the server. Zendesk
 * calls the `loginUser` callback again whenever it needs a new token, and each
 * call after the first fetches a fresh one (they live five minutes). When the
 * backend has no Zendesk credentials it answers 404, `fetchToken` resolves
 * null, and the chat simply stays anonymous.
 *
 * Unmounting — which for `AppLayout` means the session ended — calls
 * `logoutUser`, so the next person on this browser does not inherit the
 * previous one's conversations.
 */
export interface UseSupportChatOptions {
  /** Injectable for the tests. */
  loader?: () => Promise<ZendeskApi>;
  enabled?: boolean;
  /** Resolves the messaging JWT, or null to stay anonymous. */
  fetchToken?: () => Promise<string | null>;
}

export function useSupportChat({
  loader = loadZendesk,
  enabled = isZendeskEnabled(),
  fetchToken,
}: UseSupportChatOptions = {}): SupportChatState {
  const [status, setStatus] = React.useState<WidgetStatus>(
    enabled ? 'loading' : 'unavailable',
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const api = React.useRef<ZendeskApi | null>(null);
  // In a ref so a new function identity from the caller does not reload the
  // widget; the effect reads whatever is current when it needs a token.
  const fetchTokenRef = React.useRef(fetchToken);
  fetchTokenRef.current = fetchToken;

  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let loggedIn = false;
    loader().then(
      (zE) => {
        if (cancelled) return;
        api.current = zE;
        zE('messenger:set', 'locale', 'es');
        zE('messenger', 'hide');
        zE('messenger:on', 'open', () => {
          setIsOpen(true);
          setUnread(0);
        });
        zE('messenger:on', 'close', () => {
          setIsOpen(false);
          zE('messenger', 'hide');
        });
        zE('messenger:on', 'unreadMessages', (count) => setUnread(count || 0));
        setStatus('ready');
        void identify(zE);
      },
      () => {
        if (!cancelled) setStatus('unavailable');
      },
    );

    async function identify(zE: ZendeskApi): Promise<void> {
      const fetcher = () => fetchTokenRef.current?.() ?? Promise.resolve(null);
      const first = await fetcher().catch(() => null);
      if (cancelled || !first) return;
      let unused: string | null = first;
      zE('messenger', 'loginUser', (callback) => {
        if (unused) {
          const token = unused;
          unused = null;
          callback(token);
          return;
        }
        void fetcher()
          .catch(() => null)
          .then((token) => {
            if (token) callback(token);
          });
      });
      loggedIn = true;
    }

    return () => {
      cancelled = true;
      if (loggedIn) api.current?.('messenger', 'logoutUser');
    };
  }, [enabled, loader]);

  const toggle = React.useCallback(() => {
    const zE = api.current;
    if (!zE) return;
    if (isOpen) {
      zE('messenger', 'close');
    } else {
      zE('messenger', 'show');
      zE('messenger', 'open');
    }
  }, [isOpen]);

  return { status, isOpen, unread, toggle };
}
